import React from 'react';
import { DocumentSheet, DP } from '../../components/shared/folio/DocumentSheet';

/* The GDPR notice, drawn as a folio.

   The copy is the legal text as written and is untouched by the design: the
   sheet supplies the measure, the ruled schedules and the contents in the
   margin, and every heading, clause and citation below is carried over word
   for word. The ids exist only so the contents list has somewhere to go. */

const SECTIONS: [string, string][] = [
  ['gdpr-1', '1. Definitions'],
  ['gdpr-2', '2. Data Controller'],
  ['gdpr-3', '3. Your Rights Under GDPR'],
  ['gdpr-4', '4. Categories of Personal Data We Collect'],
  ['gdpr-5', '5. Legal Basis for Processing'],
  ['gdpr-6', '6. Data Retention'],
  ['gdpr-7', '7. Data Security'],
  ['gdpr-8', '8. International Data Transfers'],
  ['gdpr-9', '9. Data Processors'],
  ['gdpr-10', '10. Exercising Your Data Protection Rights'],
  ['gdpr-11', '11. Data Breach Notification'],
  ['gdpr-12', '12. Cookies and Electronic Communications'],
  ['gdpr-13', '13. Changes to This Notice'],
  ['gdpr-14', '14. Contact Us'],
];

const GDPRPage: React.FC = () => {
  return (
    <DocumentSheet
      title="GDPR Compliance Notice"
      code="§ gdpr"
      dateline="Last updated: 21 February 2026"
      sections={SECTIONS}
      blockRows={[
        ['Sheet', 'GDPR'],
        ['Regulation', 'EU 2016/679'],
        ['Controller', 'Acanetic Ltd'],
        ['Sections', String(SECTIONS.length)],
      ]}
    >
      <p>
        Acanetic Ltd ("<strong>Acanetic</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>")
        is committed to protecting your personal data and ensuring full compliance with Regulation (EU) 2016/679
        (the "<strong>General Data Protection Regulation</strong>" or "<strong>GDPR</strong>"), the UK General Data
        Protection Regulation ("<strong>UK GDPR</strong>"), the Data Protection Act 2018, and the Privacy and
        Electronic Communications Regulations 2003 (as amended). This GDPR Compliance Notice (the "<strong>Notice</strong>")
        sets out how we collect, use, store, and protect your personal data, and explains your rights as a data subject.
      </p>

      <h2 id="gdpr-1">1. Definitions</h2>
      <p>For the purposes of this Notice, the following definitions shall apply:</p>
      <ul>
        <li><strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person (a "Data Subject"), as defined in Article 4(1) GDPR.</li>
        <li><strong>"Processing"</strong> means any operation or set of operations performed on Personal Data, whether or not by automated means, as defined in Article 4(2) GDPR.</li>
        <li><strong>"Controller"</strong> means the natural or legal person which determines the purposes and means of the Processing of Personal Data. For the purposes of this Notice, the Controller is Acanetic Ltd.</li>
        <li><strong>"Processor"</strong> means a natural or legal person which processes Personal Data on behalf of the Controller.</li>
        <li><strong>"Platform"</strong> means the Acanetic website, web application, and all associated digital services operated by Acanetic Ltd.</li>
        <li><strong>"User"</strong> means any natural person who accesses or uses the Platform, including job seekers, employers, and institutional representatives.</li>
        <li><strong>"Supervisory Authority"</strong> means the Information Commissioner's Office (ICO) in the United Kingdom, or the relevant data protection authority in the EU Member State of the Data Subject's habitual residence.</li>
      </ul>

      <h2 id="gdpr-2">2. Data Controller</h2>
      <p>
        Acanetic Ltd is the Controller responsible for your Personal Data within the meaning of Article 4(7) GDPR.
        Our registered details are as follows:
      </p>
      <ul>
        <li><strong>Company Name:</strong> Acanetic Ltd</li>
        <li><strong>Registered Address:</strong> Cambridge Science Park, Cambridge, CB4 0WZ, United Kingdom</li>
        <li><strong>Data Protection Contact:</strong> privacy@acanetic.co.uk</li>
        <li><strong>Telephone:</strong> +44 20 7123 4567</li>
      </ul>

      <h2 id="gdpr-3">3. Your Rights Under GDPR</h2>
      <p>
        Pursuant to Chapter III of the GDPR (Articles 12–23), you are entitled to exercise the following rights
        in relation to the Personal Data we hold about you:
      </p>
      <ul>
        <li>
          <strong>Right of Access (Article 15):</strong> You have the right to obtain confirmation as to whether
          Personal Data concerning you is being processed, and where that is the case, to request access to the
          Personal Data and the following information: the purposes of the Processing, the categories of Personal
          Data concerned, the recipients or categories of recipients, the envisaged retention period, and the
          existence of your other rights.
        </li>
        <li>
          <strong>Right to Rectification (Article 16):</strong> You have the right to obtain without undue delay
          the rectification of inaccurate Personal Data concerning you. Taking into account the purposes of the
          Processing, you have the right to have incomplete Personal Data completed, including by means of providing
          a supplementary statement.
        </li>
        <li>
          <strong>Right to Erasure (Article 17):</strong> You have the right to obtain the erasure of Personal Data
          concerning you without undue delay where one of the following grounds applies: the Personal Data is no
          longer necessary in relation to the purposes for which it was collected; you withdraw consent on which
          the Processing is based; you object to the Processing and there are no overriding legitimate grounds;
          the Personal Data has been unlawfully processed; or the Personal Data must be erased for compliance with
          a legal obligation. This right is subject to the exceptions set out in Article 17(3) GDPR.
        </li>
        <li>
          <strong>Right to Restriction of Processing (Article 18):</strong> You have the right to obtain restriction
          of Processing where you contest the accuracy of the Personal Data, the Processing is unlawful and you
          oppose erasure, we no longer need the Personal Data but you require it for the establishment, exercise,
          or defence of legal claims, or you have objected to Processing pending verification of whether our
          legitimate grounds override your own.
        </li>
        <li>
          <strong>Right to Data Portability (Article 20):</strong> You have the right to receive the Personal Data
          you have provided to us in a structured, commonly used, and machine-readable format, and to transmit
          that data to another controller without hindrance, where the Processing is based on consent or on a
          contract and the Processing is carried out by automated means.
        </li>
        <li>
          <strong>Right to Object (Article 21):</strong> You have the right to object at any time to the Processing
          of Personal Data concerning you which is based on legitimate interests (Article 6(1)(f) GDPR), including
          profiling based on those provisions. We shall no longer process the Personal Data unless we demonstrate
          compelling legitimate grounds for the Processing which override your interests, rights, and freedoms,
          or for the establishment, exercise, or defence of legal claims. Where Personal Data is processed for
          direct marketing purposes, you have the right to object at any time, and the data shall no longer be
          processed for such purposes.
        </li>
        <li>
          <strong>Right to Withdraw Consent (Article 7(3)):</strong> Where Processing is based on your consent,
          you have the right to withdraw that consent at any time. The withdrawal of consent shall not affect
          the lawfulness of Processing based on consent before its withdrawal.
        </li>
        <li>
          <strong>Rights Related to Automated Decision-Making (Article 22):</strong> You have the right not to be
          subject to a decision based solely on automated Processing, including profiling, which produces legal
          effects concerning you or similarly significantly affects you, except where such decision is necessary
          for entering into, or performance of, a contract, is authorised by Union or Member State law, or is
          based on your explicit consent.
        </li>
      </ul>

      <h2 id="gdpr-4">4. Categories of Personal Data We Collect</h2>
      <p>We collect and process the following categories of Personal Data in the course of providing our services:</p>
      <ul>
        <li>
          <strong>Identity Data:</strong> Full name, email address, telephone number, and such other
          identifying information as may be provided upon account registration.
        </li>
        <li>
          <strong>Profile Data:</strong> Professional information including, without limitation, curriculum vitae,
          academic qualifications, skills, employment history, educational background, and institutional affiliations.
        </li>
        <li>
          <strong>Usage Data:</strong> Technical data relating to your interaction with the Platform, including
          pages visited, features accessed, timestamps, and session duration.
        </li>
        <li>
          <strong>Marketing and Communications Data:</strong> Your preferences in respect of receiving communications
          from us, including opt-in and opt-out records.
        </li>
        <li>
          <strong>Application Data:</strong> All information submitted in connection with job applications
          made through the Platform, including cover letters, supporting documents, and degree certificates.
        </li>
      </ul>

      <h2 id="gdpr-5">5. Legal Basis for Processing</h2>
      <p>
        We process your Personal Data on one or more of the following legal bases, as set out in Article 6(1) GDPR:
      </p>
      <ul>
        <li>
          <strong>Consent (Article 6(1)(a)):</strong> Where you have given clear, informed, and unambiguous
          consent for us to process your Personal Data for one or more specific purposes. You may withdraw
          your consent at any time by contacting us at privacy@acanetic.co.uk.
        </li>
        <li>
          <strong>Performance of a Contract (Article 6(1)(b)):</strong> Where Processing is necessary for the
          performance of a contract to which you are a party, or in order to take steps at your request prior
          to entering into a contract. This includes the provision of our Platform services, management of
          your account, and facilitation of job applications.
        </li>
        <li>
          <strong>Legitimate Interests (Article 6(1)(f)):</strong> Where Processing is necessary for the purposes
          of the legitimate interests pursued by us or by a third party, provided that such interests are not
          overridden by your fundamental rights and freedoms. Our legitimate interests include: the operation,
          improvement, and security of the Platform; fraud prevention; and internal administration.
        </li>
        <li>
          <strong>Legal Obligation (Article 6(1)(c)):</strong> Where Processing is necessary for compliance with
          a legal obligation to which we are subject, including without limitation obligations under tax law,
          employment law, and anti-fraud legislation.
        </li>
      </ul>

      <h2 id="gdpr-6">6. Data Retention</h2>
      <p>
        We retain your Personal Data only for so long as is necessary to fulfil the purposes for which it
        was collected, in accordance with our internal data retention policies and applicable legal, accounting,
        and regulatory requirements. The following retention periods apply:
      </p>
      <ul>
        <li><strong>Account Data:</strong> Retained for the duration of your active account, and thereafter for a period of three (3) years following the last recorded account activity, after which it shall be securely deleted or anonymised.</li>
        <li><strong>Application Data:</strong> Retained for a period of two (2) years from the date of application submission, unless a longer retention period is required by law or requested by you.</li>
        <li><strong>Marketing and Communications Data:</strong> Retained until such time as consent is withdrawn by you, following which your data shall be suppressed from marketing activities without undue delay.</li>
        <li><strong>Usage Data:</strong> Retained for a period of two (2) years from the date of collection, after which it shall be anonymised or deleted.</li>
      </ul>
      <p>
        Upon expiry of the applicable retention period, or upon your valid request for erasure, we shall securely
        delete or irreversibly anonymise your Personal Data in accordance with Article 17 GDPR, unless continued
        retention is required or permitted by applicable law.
      </p>

      <h2 id="gdpr-7">7. Data Security</h2>
      <p>
        In accordance with Article 32 GDPR, we implement appropriate technical and organisational measures
        to ensure a level of security appropriate to the risk of the Processing, including, as appropriate:
      </p>
      <ul>
        <li>Encryption of Personal Data in transit (TLS/SSL) and at rest;</li>
        <li>Regular security assessments, vulnerability testing, and system updates;</li>
        <li>Role-based access controls and multi-factor authentication mechanisms;</li>
        <li>Ongoing staff training on data protection obligations and information security best practices;</li>
        <li>Regular review and testing of the effectiveness of technical and organisational measures for ensuring the security of the Processing.</li>
      </ul>

      <h2 id="gdpr-8">8. International Data Transfers</h2>
      <p>
        In the course of providing our services, we may transfer your Personal Data to countries outside the
        European Economic Area ("<strong>EEA</strong>") and the United Kingdom. Where such transfers occur,
        we ensure that appropriate safeguards are in place in accordance with Chapter V of the GDPR (Articles 44–49),
        including but not limited to:
      </p>
      <ul>
        <li>Transfers to countries that have been the subject of an adequacy decision by the European Commission pursuant to Article 45 GDPR;</li>
        <li>Standard Contractual Clauses ("<strong>SCCs</strong>") as adopted by the European Commission pursuant to Article 46(2)(c) GDPR, supplemented by transfer impact assessments where appropriate;</li>
        <li>Binding Corporate Rules approved pursuant to Article 47 GDPR;</li>
        <li>Approved certification mechanisms pursuant to Article 42 GDPR, together with binding and enforceable commitments of the controller or processor in the third country.</li>
      </ul>
      <p>
        You may request a copy of the safeguards we have put in place in respect of international data transfers
        by contacting us at privacy@acanetic.co.uk.
      </p>

      <h2 id="gdpr-9">9. Data Processors</h2>
      <p>
        We engage the following third-party service providers as data Processors within the meaning of Article 4(8)
        GDPR. Each Processor is granted access only to the minimum Personal Data necessary for the performance
        of their specific function:
      </p>

      <div className={`${DP}-table-wrap`}>
        <table>
          <thead>
            <tr>
              <th>Processor</th>
              <th>Purpose</th>
              <th>Personal Data Shared</th>
              <th>Location of Processing</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Resend</strong></td>
              <td>Transactional email delivery, including account verification, platform notifications, and password reset communications</td>
              <td>Email address, first name</td>
              <td>United States (safeguarded by SCCs)</td>
            </tr>
            <tr>
              <td><strong>Cloudinary</strong></td>
              <td>Cloud-based file storage and content delivery for user-uploaded documents and images, including CVs, profile photographs, institutional logos, and degree certificates</td>
              <td>Uploaded files (documents and images)</td>
              <td>United States / Global CDN (safeguarded by SCCs)</td>
            </tr>
            <tr>
              <td><strong>MongoDB Atlas</strong></td>
              <td>Primary database hosting for all Platform data, including user accounts, job listings, applications, and messaging</td>
              <td>All user, job, application, and messaging data</td>
              <td>EU/UK region</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        All Processors are bound by written Data Processing Agreements ("<strong>DPAs</strong>") entered into
        pursuant to Article 28 GDPR, which impose obligations on the Processor in respect of the security of
        Personal Data, the prohibition of sub-processing without our prior written authorisation, data breach
        notification requirements, and the obligation to delete or return all Personal Data upon termination
        of the processing engagement.
      </p>

      <h2 id="gdpr-10">10. Exercising Your Data Protection Rights</h2>
      <p>
        To exercise any of your rights set out in Section 3 of this Notice, or to make any enquiry
        concerning our data protection practices, please contact us using the details below:
      </p>
      <ul>
        <li><strong>Email:</strong> privacy@acanetic.co.uk</li>
        <li><strong>Postal Address:</strong> Data Protection, Acanetic Ltd, Cambridge Science Park, Cambridge, CB4 0WZ, United Kingdom</li>
      </ul>
      <p>
        We shall respond to your request without undue delay, and in any event within one (1) calendar month
        of receipt of the request, in accordance with Article 12(3) GDPR. Where requests are complex or numerous,
        this period may be extended by a further two (2) months, in which case we shall inform you of such
        extension and the reasons for the delay within one (1) month of receipt of the request.
      </p>
      <p>
        There is no fee for exercising your rights, save where requests are manifestly unfounded or excessive,
        in which case we may charge a reasonable fee or refuse to act on the request, in accordance with
        Article 12(5) GDPR.
      </p>
      <p>
        If you are not satisfied with our response, or if you believe that we are processing your Personal Data
        in a manner that is not compliant with applicable data protection law, you have the right to lodge a
        complaint with the relevant Supervisory Authority. In the United Kingdom, the Supervisory Authority is
        the Information Commissioner's Office (ICO), which can be contacted at{' '}
        <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">https://ico.org.uk</a>.
      </p>

      <h2 id="gdpr-11">11. Data Breach Notification</h2>
      <p>
        In accordance with Articles 33 and 34 GDPR, we maintain documented procedures for the detection,
        reporting, and investigation of Personal Data breaches. In the event of a Personal Data breach:
      </p>
      <ul>
        <li>
          We shall notify the relevant Supervisory Authority (the ICO) without undue delay and, where feasible,
          not later than seventy-two (72) hours after becoming aware of the breach, unless the breach is unlikely
          to result in a risk to the rights and freedoms of natural persons (Article 33(1) GDPR).
        </li>
        <li>
          Where the breach is likely to result in a high risk to the rights and freedoms of natural persons,
          we shall communicate the breach to the affected Data Subjects without undue delay (Article 34(1) GDPR),
          including by direct notification via email.
        </li>
      </ul>
      <p>Our breach notification shall include the following particulars:</p>
      <ul>
        <li>A description of the nature of the Personal Data breach, including, where possible, the categories and approximate number of Data Subjects concerned;</li>
        <li>The name and contact details of the data protection contact from whom further information may be obtained;</li>
        <li>A description of the likely consequences of the breach;</li>
        <li>A description of the measures taken or proposed to be taken to address the breach, including, where appropriate, measures to mitigate its possible adverse effects;</li>
        <li>Recommended steps that affected Data Subjects may take to protect themselves.</li>
      </ul>

      <h2 id="gdpr-12">12. Cookies and Electronic Communications</h2>
      <p>
        In accordance with the Privacy and Electronic Communications Regulations 2003 (implementing Directive
        2002/58/EC, the "<strong>ePrivacy Directive</strong>"), we use a single strictly necessary cookie for
        authentication purposes. This cookie is essential for the operation of the Platform and enables you to
        remain securely logged in during your session. As a strictly necessary cookie, it is exempt from the
        consent requirement under Regulation 6 of the Privacy and Electronic Communications Regulations 2003.
      </p>
      <p>
        We do not deploy any third-party tracking cookies, analytics cookies, or marketing cookies on the
        Platform. You may manage your cookie preferences through the cookie consent mechanism provided on the
        Platform or through your browser settings. For further information, please refer to our Privacy Policy.
      </p>

      <h2 id="gdpr-13">13. Changes to This Notice</h2>
      <p>
        We reserve the right to amend or update this Notice at any time to reflect changes in our data processing
        practices or applicable legal requirements. Where we make material changes to this Notice, we shall notify
        you by email to the address associated with your account, or by means of a prominent notice on the Platform,
        prior to the changes taking effect. We encourage you to review this Notice periodically. Your continued
        use of the Platform following the posting of changes constitutes your acknowledgement of such changes.
      </p>

      <h2 id="gdpr-14">14. Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests regarding this GDPR Compliance Notice, our data
        protection practices, or the exercise of your rights, please contact us at:
      </p>
      <ul>
        <li><strong>Email:</strong> privacy@acanetic.co.uk</li>
        <li><strong>Telephone:</strong> +44 20 7123 4567</li>
        <li><strong>Postal Address:</strong> Acanetic Ltd, Cambridge Science Park, Cambridge, CB4 0WZ, United Kingdom</li>
      </ul>
    </DocumentSheet>
  );
};

export default GDPRPage;
